import { FC } from "react";
import { useTranslation } from "react-i18next";
import { CaseCreation } from "types";
import { DateView } from "utils/helper";

type Props = {
  caseData: CaseCreation;
};

const CourtList: FC<Props> = ({ caseData }) => {
  const { t } = useTranslation();

  const formatStatus = (status?: string) => {
    if (!status) return t("na");

    const fallback = status
      ?.replace(/_/g, " ")
      .replace(/\b\w/g, (c: any) => c.toUpperCase());

    return t(`status_${status.toLowerCase()}`, { defaultValue: fallback });
  };

  const formatDecision = (decision?: string) => {
    if (!decision) return t("na");

    const fallback = decision
      ?.replace(/_/g, " ")
      .replace(/\b\w/g, (c: any) => c.toUpperCase());

    return t(`decision_${decision.toLowerCase()}`, { defaultValue: fallback });
  };

  // Whole-day difference between today and the hearing date (negative = past).
  const daysUntil = (date?: string | Date | null) => {
    if (!date) return null;
    const target = new Date(date);
    if (isNaN(target.getTime())) return null;
    const today = new Date();
    target.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return Math.round((target.getTime() - today.getTime()) / 86400000);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-2 h-8 bg-red-600 rounded-full"></div>
          <h3 className="text-lg font-semibold text-gray-900">{t("ioc_court_files")}</h3>
        </div>

        {caseData?.opening_case_files &&
        Array.isArray(caseData.opening_case_files) &&
        caseData.opening_case_files.length > 0 ? (
          <div className="space-y-6">
            {caseData.opening_case_files.map((file: any, idx: number) => (
              <div
                key={file.id || idx}
                className="border border-gray-200 rounded-lg p-6"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <span className="text-2xl">🏛️</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {t("ioc_court_file_number", {
                          number: file.court_registration_number || file.id,
                        })}
                      </h3>
                      <p className="text-gray-600 mt-1">
                        {t("ioc_court_case_details_and_information")}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {file.is_venue_changed && (
                      <span className="px-3 py-1 text-xs font-medium rounded bg-amber-100 text-amber-800 border border-amber-300">
                        {t("ioc_venue_changed")}
                      </span>
                    )}
                    <span className="px-3 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800 border border-blue-300">
                      {formatStatus(file.status)}
                    </span>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Prominent first-court-hearing date with remaining-days countdown. */}
                  {file?.first_court_hearing_date &&
                    (() => {
                      const remaining = daysUntil(file.first_court_hearing_date);
                      const countdown =
                        remaining === null ? null : remaining > 0 ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300">
                            ⏳ {remaining}{" "}
                            {remaining === 1 ? t("day_left") : t("days_left")}
                          </span>
                        ) : remaining === 0 ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                            🔔 {t("ioc_hearing_today")}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-gray-100 text-gray-700 border border-gray-300">
                            ✓ {t("ioc_hearing_passed")} (
                            {Math.abs(remaining)}{" "}
                            {Math.abs(remaining) === 1
                              ? t("day_ago")
                              : t("days_ago")}
                            )
                          </span>
                        );
                      return (
                        <div className="rounded-xl border border-blue-200 bg-blue-50 p-5">
                          <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shrink-0">
                                <span className="text-2xl">📅</span>
                              </div>
                              <div>
                                <p className="text-xs font-medium uppercase tracking-wide text-blue-700">
                                  {t("ioc_first_court_hearing_date")}
                                </p>
                                <p className="text-lg font-bold text-gray-900">
                                  {DateView(file.first_court_hearing_date)}
                                </p>
                              </div>
                            </div>
                            {countdown}
                          </div>
                        </div>
                      );
                    })()}

                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                      {t("ioc_case_file_information")}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[
                        {
                          label: t("ioc_court_registration_no"),
                          value: file?.court_registration_number,
                        },
                        {
                          label: t("ioc_incident_registration_no"),
                          value: file?.incident_registration_no,
                        },
                        {
                          label: t("crime_reference_no"),
                          value: file?.crime_refernce_number,
                        },
                        {
                          label: t("prosecutor_registration_no"),
                          value: file?.prosecutor_registration_number,
                        },
                        {
                          label: t("ioc_court_hearing_place"),
                          value: file?.court_hearing_place,
                        },
                        {
                          label: t("ioc_court_level"),
                          value: file?.court_level,
                        },
                        {
                          label: t("court_type"),
                          value: file?.court_type,
                        },
                        {
                          label: t("assign_prosecutor"),
                          value: file?.assign_prosecutor,
                        },
                        {
                          label: t("ioc_reassign_prosecutor"),
                          value: file?.reassign_prosecutor,
                        },
                      ]
                        .filter((info) => info.value)
                        .map((info, index) => (
                          <div key={index}>
                            <span className="text-xs font-medium text-gray-600">
                              {info.label}
                            </span>
                            <p className="text-sm font-medium text-gray-900 mt-1">
                              {info.value}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">
                      {t("ioc_court_location")}
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                          { label: t("city"), value: file?.city },
                          { label: t("woreda"), value: file?.woreda },
                          { label: t("ioc_sub_city"), value: file?.sub_city },
                          { label: t("zone"), value: file?.zone },
                        ]
                          .filter((info) => info.value)
                          .map((info, index) => (
                            <div
                              key={index}
                              className="flex items-baseline gap-2"
                            >
                              <span className="text-xs font-medium text-gray-600">
                                {info.label}:
                              </span>
                              <span className="text-sm text-gray-900">
                                {info.value}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>

                  {file?.pre_judgements &&
                    Array.isArray(file.pre_judgements) &&
                    file.pre_judgements.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-4">
                          {t("ioc_pre_judgements")}
                        </h4>
                        <div className="space-y-3">
                          {file.pre_judgements.map((pj: any, pjIdx: number) => (
                            <div
                              key={pjIdx}
                              className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                            >
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                  <span className="font-semibold text-blue-600">
                                    {pj.defendant?.defendant_number}
                                  </span>
                                </div>
                                <div>
                                  <h5 className="font-medium text-gray-900">
                                    {pj.defendant?.defendant_info?.first_name}{" "}
                                    {pj.defendant?.defendant_info?.middle_name}{" "}
                                    {pj.defendant?.defendant_info?.last_name}
                                  </h5>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <span className="text-xs font-medium text-gray-600">
                                    {t("decision")}
                                  </span>
                                  <p className="text-sm text-gray-900 mt-1">
                                    {formatDecision(pj.decision)}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-xs font-medium text-gray-600">
                                    {t("decision_date")}
                                  </span>
                                  <p className="text-sm text-gray-900 mt-1">
                                    {pj.decision_date
                                      ? DateView(pj.decision_date)
                                      : t("na")}
                                  </p>
                                </div>
                                <div className="md:col-span-2">
                                  <span className="text-xs font-medium text-gray-600">
                                    {t("summary")}
                                  </span>
                                  <p className="text-sm text-gray-900 mt-1">
                                    {pj.summary || t("na")}
                                  </p>
                                </div>
                                <div className="md:col-span-2">
                                  <span className="text-xs font-medium text-gray-600">
                                    {t("remarks")}
                                  </span>
                                  <p className="text-sm text-gray-900 mt-1">
                                    {pj.remarks || t("na")}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
            {t("ioc_no_court_files_found")}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourtList;
